import { FieldKey } from '../../context/field-keys.js';
import { defaultRound } from '../../shared/round.js';
import { isAreaUom } from './uom-gates.js';
/**
 * SA4 — prefer-Actual-over-Reported price-per-sqft from NRA, ported from
 * `sale-auto-calc-listeners.ts` listenerSPPSqFtFromNRA$ (~242-283) +
 * `calculateQuotingPricePerSqFtFromNRA` (~746-772) and `@miq/utils`
 * `pickPreferredPrice` (projects/miq/utils/src/lib/uom-calculator/amount-operations.ts).
 *
 *   sales_price_per_square_foot = pickPreferredPrice(actual_sales_price, reported_sale_price) / net_rentable_area
 *
 * The selection is the essence: Actual wins when it is a finite number > 0, else Reported
 * (same test), else null. Note the legacy `isNumber` is a strict `typeof === 'number'`
 * check — numeric strings do NOT qualify — and 0/negative values are "not present".
 *
 * Gate (verbatim): preferred price non-null AND nra non-null AND isAreaUOMCheck(nraUOM);
 * no currency check in this direction. Gate fail -> no write. NRA 0 -> divide -> Infinity
 * -> write null (legacy patch null). The result-UOM write (per-sqft UOM := the preferred
 * price's own UOM, picked by `preferred === actual ? actualUOM : reportedUOM`) is host-owned (T2.7).
 */
/** Legacy `isNumber`: strict number typeof + not NaN (numeric strings excluded). */
export function isPresentPrice(value) {
  return typeof value === 'number' && !isNaN(value);
}
/**
 * Exact port of `@miq/utils pickPreferredPrice`: first candidate that is a number > 0 wins
 * (actual preferred over reported); null when neither qualifies.
 */
export function pickPreferredPrice(actual, reported) {
  const hasActual = isPresentPrice(actual) && actual > 0;
  const hasReported = isPresentPrice(reported) && reported > 0;
  if (hasActual) {
    return actual;
  }
  if (hasReported) {
    return reported;
  }
  return null;
}
/** SA4: sales_price_per_square_foot = (actual_sales_price || reported_sale_price) / net_rentable_area. */
export const pricePerSqFtFromPreferredPriceRule = {
  id: 'sale.price-per-sqft.from-preferred-price-nra',
  inputs: [FieldKey.SalePriceActual, FieldKey.SalePrice, FieldKey.NetRentableArea, FieldKey.NetRentableAreaUom],
  outputs: [FieldKey.PricePerSqFt],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const preferred = pickPreferredPrice(ctx.get(FieldKey.SalePriceActual), ctx.get(FieldKey.SalePrice));
    const nraRaw = ctx.get(FieldKey.NetRentableArea);
    // Legacy: isEveryControlNotNull([nra, salePriceValue]) && isAreaUOMCheck(nraUOM), else no patch.
    if (preferred == null || nraRaw == null || !isAreaUom(ctx.get(FieldKey.NetRentableAreaUom))) {
      return;
    }
    const result = preferred / Number(nraRaw);
    ctx.set(FieldKey.PricePerSqFt, Number.isFinite(result) ? round(result, FieldKey.PricePerSqFt) : null);
  }
};
export const preferredPriceRules = [pricePerSqFtFromPreferredPriceRule];
